import { useEffect, useRef } from 'react'
import Button from '../ui/buttons/Button'
import Logo from '../ui/Logo'
import LoadingBar from '../ui/LoadingBar'
import clsx from 'clsx'
import { useLoadingProgressController } from '@/src/hooks/useLoadingProgressController'

export type SplashScreenDebugStatus = {
    threeD: string
    ktx2: string
    audio: string
    video: string
    cache: string
    scene: string
    failures: string
}

export type SplashScreenProps = {
    // 0–100 from asset preload.
    assetProgress: number
    loadingReady: boolean
    // Something needed to leave the splash could not be loaded.
    isLoadingError: boolean
    onRetryLoading: () => void
    // Fired once when `loadingReady` and min dwell time have passed.
    onComplete: () => void
    debugStatus?: SplashScreenDebugStatus
}

export default function SplashScreen({
    assetProgress,
    loadingReady,
    isLoadingError,
    onRetryLoading,
    onComplete,
    debugStatus,
}: SplashScreenProps) {
    const completedRef = useRef(false)
    const { displayProgress, durationMS, isComplete } = useLoadingProgressController(
        assetProgress,
        loadingReady,
    )

    useEffect(() => {
        if (!isComplete || completedRef.current) {
            return
        }
        completedRef.current = true
        onComplete()
    }, [isComplete, onComplete])

    return (
        <>
            <img src='/images/BackgroundTrack.png' className='absolute inset-0 w-full h-full object-cover' />
            <div
                className={clsx(
                    'absolute inset-0 flex flex-col justify-between items-center',
                    'px-4 py-6',
                    'stele:px-16 stele:py-24'
                )}
            >
                <Logo variant='stacked' />
                <div className={clsx(
                    'flex flex-col justify-center items-center w-full',
                    'max-w-143.5 gap-2',
                    'stele:max-w-412 stele:gap-8'
                )}>
                    {isLoadingError && (
                        <Button onClick={onRetryLoading}>
                            Erneut versuchen
                        </Button>
                    )}
                    {import.meta.env.DEV && debugStatus ? (
                        <div className='bg-black/55 px-3 stele:px-6 py-2 stele:py-4 rounded-2xl w-full font-mono text-[10px] text-white stele:text-[24px] leading-tight'>
                            {`3D ${debugStatus.threeD} | ktx2 ${debugStatus.ktx2} | audio ${debugStatus.audio} | video ${debugStatus.video} | cache ${debugStatus.cache} | scene ${debugStatus.scene}`}
                            {debugStatus.failures ? (
                                <div className='mt-1 stele:mt-3 text-red-300 break-all'>
                                    {debugStatus.failures}
                                </div>
                            ) : null}
                        </div>
                    ) : null}
                    <LoadingBar progress={displayProgress} duration={durationMS} />
                    <img src='/images/PoweredbyNeueLeben.svg' alt='Neue Leben Logo' className='w-[106.5px] h-6.75 stele:w-106.5 stele:h-27' />
                </div>
            </div >
        </>
    )
}
